import { realmAvailable } from './realmBridge';
import { deleteRealmDoc, queryRealm, upsertRealmDocs } from './syncService';
import { isNetworkFailure } from './outboundQueue';
import { normalizeLiveTrackingItem } from '../utils/liveTrackingLocal';
import {
  deleteLiveTrackingItem,
  markLiveTrackingOpened,
  reorderLiveTrackingItems,
  updateLiveTrackingItem,
  upsertLiveTrackingItem,
} from '../services/liveTrackingService';

const isPendingRow = (item) => {
  const id = String(item?.id || item?._id || '');
  return item?.syncStatus === 'pending'
    || id.startsWith('pending-')
    || Boolean(item?.outboundOp);
};

export const persistLiveTrackingItemsToRealm = async (items) => {
  if (!(await realmAvailable())) return;
  const list = (Array.isArray(items) ? items : [items])
    .filter(Boolean)
    .map(normalizeLiveTrackingItem)
    .filter((item) => item.id);
  if (!list.length) return;
  await upsertRealmDocs('LiveTrackingItem', list);
};

export const readLiveTrackingItemsFromRealm = async () => {
  if (!(await realmAvailable())) return null;
  const rows = await queryRealm({ type: 'LiveTrackingItem' });
  if (!rows.length) return [];
  return rows
    .map(normalizeLiveTrackingItem)
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
};

export const deleteLiveTrackingItemFromRealm = async (id) => {
  if (!id || !(await realmAvailable())) return;
  await deleteRealmDoc('LiveTrackingItem', id);
};

export const pruneLiveTrackingItemsNotIn = async (keepIds = []) => {
  if (!(await realmAvailable())) return;
  const keep = new Set((keepIds || []).filter(Boolean).map(String));
  const rows = await readLiveTrackingItemsFromRealm();
  if (!rows) return;
  await Promise.all(rows.map(async (row) => {
    const id = String(row.id || '');
    if (!id || keep.has(id) || isPendingRow(row)) return;
    await deleteLiveTrackingItemFromRealm(id);
  }));
};

export const replaceLiveTrackingFromBootstrap = async (items) => {
  const list = (Array.isArray(items) ? items : []).map(normalizeLiveTrackingItem).filter((item) => item.id);
  if (list.length) await persistLiveTrackingItemsToRealm(list);
  await pruneLiveTrackingItemsNotIn(list.map((item) => item.id));
};

const toUpsertBody = (item) => ({
  itemType: item.itemType,
  taskId: item.taskId || undefined,
  sourceMessageId: item.sourceMessageId || undefined,
  sourceChannelId: item.sourceChannelId || undefined,
  title: item.title,
  info: item.info,
  boardLane: item.boardLane,
  assigneeUserId: item.assigneeUserId || undefined,
  assigneeName: item.assigneeName || undefined,
  seenSignature: item.seenSignature || undefined,
});

const synced = (item) => ({
  ...item,
  syncStatus: 'synced',
  outboundOp: null,
  outboundRequest: null,
});

export const flushLiveTrackingOutbound = async () => {
  if (!(await realmAvailable())) return;
  const rows = await readLiveTrackingItemsFromRealm();
  if (!rows?.length) return;

  const visible = [];
  let needsReorder = false;

  for (const row of rows) {
    const id = String(row.id || '');
    const op = String(row.outboundOp || '');
    try {
      if (op === 'delete') {
        if (id.startsWith('pending-')) {
          await deleteLiveTrackingItemFromRealm(id);
        } else {
          await deleteLiveTrackingItem(id);
          await deleteLiveTrackingItemFromRealm(id);
        }
        continue;
      }
      if (id.startsWith('pending-') || op === 'create') {
        const saved = normalizeLiveTrackingItem(await upsertLiveTrackingItem(row.outboundRequest || toUpsertBody(row)));
        await deleteLiveTrackingItemFromRealm(id);
        await persistLiveTrackingItemsToRealm(synced({ ...saved, sortOrder: row.sortOrder }));
        visible.push(synced({ ...saved, sortOrder: row.sortOrder }));
        needsReorder = true;
        continue;
      }
      if (op === 'opened') {
        const saved = normalizeLiveTrackingItem(
          await markLiveTrackingOpened(id, row.seenSignature),
        );
        await persistLiveTrackingItemsToRealm(synced(saved));
        visible.push(synced(saved));
        continue;
      }
      if (op === 'update') {
        const saved = normalizeLiveTrackingItem(
          await updateLiveTrackingItem(id, row.outboundRequest || toUpsertBody(row)),
        );
        await persistLiveTrackingItemsToRealm(synced({ ...saved, sortOrder: row.sortOrder }));
        visible.push(synced({ ...saved, sortOrder: row.sortOrder }));
        continue;
      }
      if (op === 'reorder') needsReorder = true;
      visible.push(row);
    } catch (error) {
      if (!isNetworkFailure(error)) {
        console.warn('[realm] live tracking flush failed', error?.message || error);
      }
      if (op !== 'delete') visible.push(row);
    }
  }

  if (needsReorder && visible.length) {
    try {
      const ordered = visible
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const saved = (await reorderLiveTrackingItems(
        ordered.map((item) => item.id),
        Object.fromEntries(ordered.map((item) => [item.id, item.boardLane || 'TEAM'])),
      )).map(normalizeLiveTrackingItem);
      if (saved.length) await persistLiveTrackingItemsToRealm(saved.map(synced));
    } catch (error) {
      if (!isNetworkFailure(error)) {
        console.warn('[realm] live tracking reorder flush failed', error?.message || error);
      }
    }
  }
};
